import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { __ } from '@wordpress/i18n';
import CptTable from './CptTable';
import { orgColumns } from './CptTable/columns/orgs';
import { apiFetch, fetchTableRows } from '../scripts/api';
import { splitValues, downloadCsv } from '../scripts/exportCsv';

const taxNames = ( c ) => c?.nodes?.map( ( n ) => n.name ).join( '; ' ) ?? '';

function exportOrgsToCSV( rows ) {
	const headers = [
		__( 'Name', 'anacrm' ),
		__( 'Territory', 'anacrm' ),
		__( 'Region', 'anacrm' ),
		__( 'Municipality', 'anacrm' ),
		__( 'Email', 'anacrm' ),
		__( 'Phone', 'anacrm' ),
		__( 'Web', 'anacrm' ),
		__( 'Category', 'anacrm' ),
		__( 'Sector', 'anacrm' ),
		__( 'Network', 'anacrm' ),
		__( 'Legal form', 'anacrm' ),
	];
	const dataRows = rows.map( ( r ) => [
		r.title ?? '',
		taxNames( r.territories ),
		taxNames( r.regions ),
		taxNames( r.cities ),
		r.contactMail ?? '',
		r.contactPhone ?? '',
		r.contactWebsite ?? '',
		taxNames( r.fields ),
		taxNames( r.sectors ),
		taxNames( r.networks ),
		taxNames( r.legalForms ),
	] );
	downloadCsv(
		headers,
		dataRows,
		`organisations-${ new Date().toISOString().slice( 0, 10 ) }.csv`
	);
}

export default function OrgTable() {
	const [ data, setData ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( null );
	const [ emailList, setEmailList ] = useState( null );
	const [ shareUrl, setShareUrl ] = useState( null );
	const [ copied, setCopied ] = useState( false );

	useEffect( () => {
		fetchTableRows( 'org' )
			.then( ( nodes ) => setData( nodes ) )
			.catch( ( err ) => setError( err.message ) )
			.finally( () => setLoading( false ) );
	}, [] );

	const handleDelete = async ( databaseId ) => {
		try {
			await apiFetch( `/anacrm/v1/org/${ databaseId }`, {
				method: 'DELETE',
			} );
			setData( ( prev ) =>
				prev.filter( ( item ) => item.databaseId !== databaseId )
			);
		} catch ( err ) {
			window.alert(
				__( 'Could not delete the organisation:', 'anacrm' ) +
					err.message
			);
		}
	};

	const handleTogglePrivate = async ( databaseId, nextPrivate ) => {
		try {
			await apiFetch( `/anacrm/v1/org/${ databaseId }/private`, {
				method: 'PATCH',
				body: JSON.stringify( { private: nextPrivate } ),
			} );
			setData( ( prev ) =>
				prev.map( ( item ) =>
					item.databaseId === databaseId
						? { ...item, private: nextPrivate }
						: item
				)
			);
		} catch ( err ) {
			window.alert(
				__( 'Could not update the organisation:', 'anacrm' ) +
					err.message
			);
		}
	};

	const handleReclaim = async ( databaseId ) => {
		try {
			const res = await apiFetch(
				`/anacrm/v1/org/${ databaseId }/reclaim`,
				{ method: 'POST' }
			);
			window.location.href = res.url;
		} catch ( err ) {
			window.alert(
				__( 'Could not reclaim the organisation:', 'anacrm' ) +
					' ' +
					err.message
			);
		}
	};

	const handleSuggest = async ( databaseId ) => {
		try {
			const res = await apiFetch(
				`/anacrm/v1/org/${ databaseId }/suggest`,
				{ method: 'POST' }
			);
			window.location.href = res.url;
		} catch ( err ) {
			window.alert(
				__( 'Could not start the suggestion:', 'anacrm' ) +
					' ' +
					err.message
			);
		}
	};

	useEffect( () => {
		const handler = () => setShareUrl( window.location.href );
		document.addEventListener( 'anacrm:share-org', handler );
		return () =>
			document.removeEventListener( 'anacrm:share-org', handler );
	}, [] );

	const handleCopyLink = useCallback( () => {
		navigator.clipboard.writeText( shareUrl ).then( () => {
			setCopied( true );
			setTimeout( () => setCopied( false ), 2000 );
		} );
	}, [ shareUrl ] );

	function handleEmailExport( rows ) {
		const emails = rows
			.map( ( r ) => splitValues( r.contactMail )[ 0 ] )
			.filter( Boolean )
			.join( ', ' );
		setEmailList( emails );
	}

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const columns = useMemo(
		() =>
			orgColumns( {
				onDelete: handleDelete,
				onTogglePrivate: handleTogglePrivate,
				onReclaim: handleReclaim,
				onSuggest: handleSuggest,
			} ),
		[]
	);

	if ( error ) {
		return (
			<div className="px-4 py-3 border-2 border-red text-red text-sm">
				{ __( 'Error loading data:', 'anacrm' ) } { error }
			</div>
		);
	}

	const filters = [
		{
			columnId: 'territories',
			label: __( 'Territory', 'anacrm' ),
			getTerms: ( row ) =>
				row.territories?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'regions',
			label: __( 'Region', 'anacrm' ),
			getTerms: ( row ) =>
				row.regions?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'cities',
			label: __( 'Municipality', 'anacrm' ),
			getTerms: ( row ) =>
				row.cities?.nodes?.map( ( n ) => n.name ) ?? [],
			allowedValues: window.anacrm?.allowedArchiveCities ?? null,
			dependsOnColumnId: 'territories',
			relationMap: window.anacrm?.cityTerritoryMap ?? {},
			disabledWhenValues:
				window.anacrm?.cityFilterDisabledTerritories ?? [],
		},
		{
			columnId: 'fields',
			label: __( 'Category', 'anacrm' ),
			getTerms: ( row ) =>
				row.fields?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'sectors',
			label: __( 'Sector', 'anacrm' ),
			getTerms: ( row ) =>
				row.sectors?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'networks',
			label: __( 'Network', 'anacrm' ),
			getTerms: ( row ) =>
				row.networks?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'legalForms',
			label: __( 'Legal form', 'anacrm' ),
			getTerms: ( row ) =>
				row.legalForms?.nodes?.map( ( n ) => n.name ) ?? [],
		},
	];

	const emailContainer = document.getElementById( 'anacrm-email-export-org' );
	const shareContainer = document.getElementById( 'anacrm-share-org' );

	return (
		<>
			<CptTable
				columns={ columns }
				data={ data }
				isLoading={ loading }
				filters={ filters }
				exportEvent="anacrm:export-org"
				onExportRows={ exportOrgsToCSV }
				emailExportEvent="anacrm:export-emails-org"
				onEmailExportRows={ handleEmailExport }
				searchPlaceholder={ __( 'Search organisations…', 'anacrm' ) }
				fitWidth
				getRowClassName={ ( row ) =>
					row.private ? 'bg-red/10' : ''
				}
			/>
			{ emailList !== null &&
				emailContainer &&
				createPortal(
					<div className="w-full px-8 py-3 bg-green flex items-center justify-between">
						<span>{ emailList }</span>
						<button
							onClick={ () => setEmailList( null ) }
							className="px-3 py-2 text-2xl hover:text-yellow cursor-pointer"
						>
							&times;
						</button>
					</div>,
					emailContainer
				) }
			{ shareUrl !== null &&
				shareContainer &&
				createPortal(
					<div className="w-full px-8 py-3 bg-green flex items-center justify-between">
						<div className="flex flex-col gap-1 min-w-0">
							<strong>{ __( 'Share', 'anacrm' ) }</strong>
							<div className="flex items-center gap-3 flex-wrap">
								<span className="text-xs">
									{ __( 'Link to share:', 'anacrm' ) }{ ' ' }
									<a
										href={ shareUrl }
										className="underline break-all"
									>
										{ shareUrl }
									</a>
								</span>
								<button
									onClick={ handleCopyLink }
									className="shrink-0 px-3 py-2 border-2 border-green-dark bg-gray-light hover:bg-yellow cursor-pointer font-bold text-xs"
								>
									{ copied
										? __( 'Copied!', 'anacrm' )
										: __( 'Copy link', 'anacrm' ) }
								</button>
							</div>
						</div>
						<button
							onClick={ () => {
								setShareUrl( null );
								setCopied( false );
							} }
							className="px-3 py-2 text-2xl hover:text-yellow cursor-pointer shrink-0"
						>
							&times;
						</button>
					</div>,
					shareContainer
				) }
		</>
	);
}
